import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { notifier } from '../../functions/notifier';
import RequestConfig from './request-config';
import {
  AUTH_TOKEN,
  Default_Language,
  LANGUAGE_HEADER_KEY,
  remoteServiceBaseApiUrl,
} from '../../constants/AppConst';
import urlJoin from 'url-join';
import { ApiError, normalizeApiError } from './api-error';
import { deleteCookie, getCookieValue } from './cookies';

export const API_ERROR_NOTIFICATION_KEY = 'global-api-error';

type HeaderValue = string | number | boolean | null | undefined;
type ApiHeaders = Record<string, HeaderValue>;

const toHeaders = (headers: unknown): ApiHeaders => {
  if (
    typeof headers !== 'object' ||
    headers === null ||
    Array.isArray(headers)
  ) {
    return {};
  }

  return { ...(headers as ApiHeaders) };
};

export const applyStandardHeaders = (
  config: RequestConfig,
  token: string | null,
  language: string | null,
): RequestConfig => {
  const headers = toHeaders(config.headers);

  if (!headers.Accept) headers.Accept = '*/*';
  if (!config.skipAuth && token) headers.Authorization = `Bearer ${token}`;
  if (!config.skipLanguage && language) {
    const languageCode = language.trim().toLowerCase().split(/[-_]/)[0];
    if (languageCode === 'ar' || languageCode === 'en') {
      headers[LANGUAGE_HEADER_KEY] = languageCode;
    }
  }

  return { ...config, headers };
};

export const getResponsePayload = <T>(response: AxiosResponse<T>): T | null =>
  response.data === '' ? null : response.data;

const showApiError = (error: ApiError): void => {
  notifier(error.message, 'error', {
    key: API_ERROR_NOTIFICATION_KEY,
    duration: error.isNetworkError ? 0 : 5,
  });
};

export default class ApiProvider {
  readonly api: AxiosInstance;

  constructor(config: RequestConfig) {
    this.api = axios.create({
      ...config,
      baseURL: urlJoin(`${remoteServiceBaseApiUrl}`, `${config.baseURL}`),
    });
    this.api.interceptors.request.use((req: AxiosRequestConfig) =>
      applyStandardHeaders(
        req as RequestConfig,
        getCookieValue(AUTH_TOKEN),
        getCookieValue(Default_Language),
      ),
    );
    this.api.interceptors.response.use(
      (res: AxiosResponse<unknown>) => {
        const payload = getResponsePayload(res);
        if (
          typeof payload === 'object' &&
          payload !== null &&
          'data' in payload &&
          typeof (payload as { data?: unknown }).data === 'string' &&
          res.config?.url !== '/set-web-fcm-token'
        ) {
          notifier((payload as { data: string }).data, 'success');
        }
        return payload;
      },
      (error: unknown) => {
        if (axios.isCancel(error)) return Promise.reject(error);

        const apiError = normalizeApiError(error);
        const axiosConfig =
          typeof error === 'object' && error !== null && 'config' in error
            ? (error as { config?: RequestConfig }).config
            : undefined;

        if (apiError.status === 401) {
          deleteCookie(AUTH_TOKEN);
        }

        if (!axiosConfig?.skipErrorNotification && apiError.status !== 401) {
          showApiError(apiError);
        }

        return Promise.reject(apiError);
      },
    );
  }

  request<T>(config: RequestConfig): Promise<T> {
    return this.api.request<unknown, T>(config);
  }
}
